/**
 * UserRuntimePane — Phase 21 per-user runtime parameters console.
 *
 * Renders 8 tabs over the per-user lexruntime OpenClaw settings surface
 * (`/api/lx/openclaw/*`, cross-user proxied via X-Target-User-Id; see
 * `lexportal/src/api/lx-openclaw-config.ts`):
 *
 *   1. Personal           — PATCH /config/user (personaSuffix, locale,
 *                            agent identity override). Kill-switch UX
 *                            on 409 USER_SETTING_DISABLED_BY_ORG.
 *   2. Channels           — channel status + force-clear-session
 *                            (handoff §4.2 — ForceClearSessionDialog).
 *   3. Notifications      — VAPID public key + push subscriptions list
 *                            + Send-test-push.
 *   4. Cron               — list jobs + run-history + run-now +
 *                            force-cancel (handoff §4.4 — only when
 *                            lastRunStatus === "running").
 *   5. Browser            — profile list + launch/force-stop (handoff
 *                            §4.1 — ForceStopProfileDialog).
 *   6. Voice              — TTS toggle + org-curated voice picker.
 *   7. Agent              — READ-ONLY view of org agent identity (the
 *                            ORG_ADMIN rail lives in lexserver, not
 *                            here — operator drills via Organizations).
 *   8. Memory & Tools     — READ-ONLY view of org-side memory + tools
 *                            policy (same: not editable from here).
 *
 * Operator-grade dialogs (ForceStop / ForceClear / ForceCancel) live in
 * `components/platform/runtime-parameters/*` and land in slice 5.
 *
 * Hard rules (handoff §10):
 *   - Do NOT cache settings — refetch on focus / tab switch / after
 *     any successful mutation.
 *   - Do NOT log the noVNC URL anywhere — `?jwt=` is a short-TTL
 *     bearer token. The Browser tab embeds the iframe (when P4.D ships)
 *     but never console.log()s the URL.
 *
 * Coordinated parallel rollout (handoff §11): tabs whose backend slice
 * hasn't shipped yet on lexruntime surface their pending state inline
 * rather than 404'ing — the UI is forward-compatible with the documented
 * endpoint shapes.
 */

import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import {
  ArrowLeft,
  Ban,
  Bot,
  Brain,
  ChevronRight,
  ClipboardList,
  Globe2,
  Headphones,
  Loader2,
  Megaphone,
  MessageSquare,
  Play,
  RefreshCw,
  Search,
  Square,
  Timer,
  User as UserIcon,
  Volume2,
  Wrench,
} from "lucide-react";
import { usePlatformApi } from "@/hooks/usePlatformApi";
import type {
  OperationalLogFilter,
  OperationalLogRecord,
} from "@/types/operational-log";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { getLexiunToken } from "@/lib/lexiun-jwt";
import {
  LiveBrowserCanvas,
  type LiveBrowserStatus,
} from "@/components/monitoring/LiveBrowserCanvas";
import {
  cancelCronJob,
  disconnectChannel,
  getBrowserStatus,
  getCronJobRuns,
  getOpenclawConfig,
  getRecentPushEvents,
  launchBrowserProfile,
  listCronJobs,
  LxOpenclawConfigError,
  patchOpenclawUserConfig,
  pauseBrowserProfile,
  resumeBrowserProfile,
  runCronJob,
  stopBrowserProfile,
  testPushSubscription,
  wipeBrowserProfileCookies,
  type BrowserProfile,
  type BrowserStatus as LxBrowserStatus,
  type ChannelId,
  type ChannelState,
  type CronJob,
  type CronRun,
  type OpenclawConfig,
  type PushEvent,
  type UserState,
  type UserStatePatch,
} from "@/api/lx-openclaw-config";

const TOKEN_GETTER = () => getLexiunToken();

function errMsg(e: unknown, fallback: string): string {
  if (e instanceof LxOpenclawConfigError) {
    const codePart = e.code ? `${e.code}: ` : "";
    const keysPart = e.disabledKeys?.length
      ? ` (keys: ${e.disabledKeys.join(", ")})`
      : "";
    return `${codePart}${e.message}${keysPart}`;
  }
  return e instanceof Error ? e.message : fallback;
}

// ─── Top-level pane ──────────────────────────────────────────────────

export function UserRuntimePaneRoute() {
  const { userId } = useParams<{ userId: string }>();
  if (!userId) {
    return (
      <div className="p-6 text-sm text-destructive">
        Missing userId in route.
      </div>
    );
  }
  return <UserRuntimePane userId={userId} />;
}

function UserRuntimePane({ userId }: { userId: string }) {
  const [config, setConfig] = useState<OpenclawConfig | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      const c = await getOpenclawConfig(TOKEN_GETTER, userId);
      setConfig(c);
      setError(null);
    } catch (e) {
      setError(errMsg(e, "Failed to load OpenClaw config"));
    } finally {
      setLoading(false);
    }
  }, [userId]);

  useEffect(() => {
    void reload();
  }, [reload]);

  return (
    <div
      className="flex flex-col gap-4 p-6"
      data-testid="user-runtime-pane"
    >
      <div className="flex items-center gap-2 flex-wrap">
        <Link to="/runtime-parameters">
          <Button variant="outline" size="sm" data-testid="user-runtime-back-btn">
            <ArrowLeft className="mr-1 h-4 w-4" /> Back
          </Button>
        </Link>
        <h2 className="flex items-center gap-2 text-lg">
          <UserIcon className="h-5 w-5 text-muted-foreground" />
          <span className="font-mono text-sm">{userId}</span>
        </h2>
        <span className="text-xs text-muted-foreground">
          (PLATFORM_ADMIN cross-user view via X-Target-User-Id)
        </span>
        <Button
          variant="outline"
          size="sm"
          onClick={() => void reload()}
          disabled={loading}
          data-testid="user-runtime-reload"
          className="ml-auto"
        >
          <RefreshCw className={`mr-1 h-4 w-4 ${loading ? "animate-spin" : ""}`} />
          Refresh
        </Button>
      </div>

      {error && (
        <div
          className="rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2 text-sm text-destructive"
          data-testid="user-runtime-error"
        >
          {error}
        </div>
      )}

      {loading && !config ? (
        <div className="flex items-center gap-2 p-12 text-sm text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin" /> Loading runtime config…
        </div>
      ) : config ? (
        <Tabs defaultValue="personal" className="flex flex-col gap-3">
          <TabsList className="flex flex-wrap gap-1">
            <TabsTrigger value="personal" data-testid="tab-personal">
              <UserIcon className="mr-1 h-3 w-3" /> Personal
            </TabsTrigger>
            <TabsTrigger value="channels" data-testid="tab-channels">
              <MessageSquare className="mr-1 h-3 w-3" /> Channels
            </TabsTrigger>
            <TabsTrigger value="push" data-testid="tab-push">
              <Megaphone className="mr-1 h-3 w-3" /> Notifications
            </TabsTrigger>
            <TabsTrigger value="cron" data-testid="tab-cron">
              <Timer className="mr-1 h-3 w-3" /> Cron
            </TabsTrigger>
            <TabsTrigger value="browser" data-testid="tab-browser">
              <Globe2 className="mr-1 h-3 w-3" /> Browser
            </TabsTrigger>
            <TabsTrigger value="voice" data-testid="tab-voice">
              <Volume2 className="mr-1 h-3 w-3" /> Voice
            </TabsTrigger>
            <TabsTrigger value="agent" data-testid="tab-agent">
              <Bot className="mr-1 h-3 w-3" /> Agent
            </TabsTrigger>
            <TabsTrigger value="memory" data-testid="tab-memory">
              <Brain className="mr-1 h-3 w-3" /> Memory &amp; Tools
            </TabsTrigger>
            <TabsTrigger value="audit" data-testid="tab-audit">
              <ClipboardList className="mr-1 h-3 w-3" /> Audit
            </TabsTrigger>
          </TabsList>

          <TabsContent value="personal">
            <PersonalTab userId={userId} config={config} onSaved={reload} />
          </TabsContent>
          <TabsContent value="channels">
            <ChannelsTab userId={userId} config={config} onChange={reload} />
          </TabsContent>
          <TabsContent value="push">
            <NotificationsTab userId={userId} config={config} />
          </TabsContent>
          <TabsContent value="cron">
            <CronTab userId={userId} />
          </TabsContent>
          <TabsContent value="browser">
            <BrowserTab userId={userId} config={config} onChange={reload} />
          </TabsContent>
          <TabsContent value="voice">
            <VoiceTab userId={userId} config={config} onSaved={reload} />
          </TabsContent>
          <TabsContent value="agent">
            <AgentTab config={config} />
          </TabsContent>
          <TabsContent value="memory">
            <MemoryToolsTab config={config} />
          </TabsContent>
          <TabsContent value="audit">
            <AuditLogTab userId={userId} />
          </TabsContent>
        </Tabs>
      ) : null}
    </div>
  );
}

// ─── Personal tab ────────────────────────────────────────────────────

function PersonalTab({
  userId,
  config,
  onSaved,
}: {
  userId: string;
  config: OpenclawConfig;
  onSaved: () => Promise<void>;
}) {
  const policy = config.org.userSettingsPolicy ?? {};
  const u = config.user ?? {};
  const [personaSuffix, setPersonaSuffix] = useState(u.personaSuffix ?? "");
  const [locale, setLocale] = useState(u.locale ?? "");
  const [agentName, setAgentName] = useState(u.agents?.main?.name ?? "");
  const [busy, setBusy] = useState(false);
  const [tabError, setTabError] = useState<string | null>(null);
  const [savedAt, setSavedAt] = useState<string | null>(null);

  const personaDisabled = policy.personaSuffix === false;
  const localeDisabled = policy.locale === false;
  const agentMainDisabled = policy.agentsMain === false;

  async function handleSave() {
    setBusy(true);
    setTabError(null);
    try {
      const patch: UserStatePatch = {};
      if (!personaDisabled) patch.personaSuffix = personaSuffix;
      if (!localeDisabled) patch.locale = locale;
      if (!agentMainDisabled) {
        patch.agents = { main: { name: agentName || undefined } };
      }
      await patchOpenclawUserConfig(TOKEN_GETTER, userId, patch);
      setSavedAt(new Date().toISOString());
      await onSaved();
    } catch (e) {
      setTabError(errMsg(e, "Failed to save personal settings"));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="flex flex-col gap-3 rounded-lg border border-border p-4" data-testid="personal-tab">
      <h3 className="text-sm font-medium">Personal preferences</h3>
      {tabError && (
        <div className="rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2 text-sm text-destructive" data-testid="personal-tab-error">
          {tabError}
        </div>
      )}
      <FieldRow
        label="Persona suffix"
        disabled={personaDisabled}
        disabledReason="Disabled by org admin (userSettingsPolicy.personaSuffix=false)"
      >
        <textarea
          rows={3}
          value={personaSuffix}
          disabled={personaDisabled || busy}
          onChange={(e) => setPersonaSuffix(e.target.value)}
          placeholder="e.g. 'I prefer concise replies.'"
          data-testid="personal-persona-suffix"
          className="w-full rounded-md border border-border bg-panel-2 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring disabled:opacity-60"
        />
      </FieldRow>
      <FieldRow
        label="Locale"
        disabled={localeDisabled}
        disabledReason="Disabled by org admin (userSettingsPolicy.locale=false)"
      >
        <Input
          value={locale}
          disabled={localeDisabled || busy}
          onChange={(e) => setLocale(e.target.value)}
          placeholder="pt-BR"
          data-testid="personal-locale"
        />
      </FieldRow>
      <FieldRow
        label="Agent name override"
        disabled={agentMainDisabled}
        disabledReason="Disabled by org admin (userSettingsPolicy.agentsMain=false)"
      >
        <Input
          value={agentName}
          disabled={agentMainDisabled || busy}
          onChange={(e) => setAgentName(e.target.value)}
          placeholder={config.org.agents?.defaultIdentity?.name ?? "Lexiun"}
          data-testid="personal-agent-name"
        />
      </FieldRow>
      <div className="flex items-center gap-2">
        <Button
          onClick={handleSave}
          disabled={busy}
          data-testid="personal-save-btn"
        >
          {busy && <Loader2 className="mr-1 h-4 w-4 animate-spin" />}
          Save
        </Button>
        {savedAt && (
          <span className="text-xs text-muted-foreground">
            Saved {new Date(savedAt).toLocaleTimeString()}
          </span>
        )}
      </div>
    </div>
  );
}

function FieldRow({
  label,
  disabled,
  disabledReason,
  children,
}: {
  label: string;
  disabled?: boolean;
  disabledReason?: string;
  children: React.ReactNode;
}) {
  return (
    <label className="flex flex-col gap-1">
      <span className="text-xs font-medium text-muted-foreground flex items-center gap-2">
        {label}
        {disabled && (
          <Badge
            variant="outline"
            title={disabledReason}
            data-testid={`field-locked-${label.toLowerCase().replace(/\s+/g, "-")}`}
          >
            locked by org
          </Badge>
        )}
      </span>
      {children}
    </label>
  );
}

// ─── Channels tab ────────────────────────────────────────────────────

const CHANNEL_IDS: ChannelId[] = ["whatsapp", "telegram", "email"];

function ChannelsTab({
  userId,
  config,
  onChange,
}: {
  userId: string;
  config: OpenclawConfig;
  onChange: () => Promise<void>;
}) {
  const user = config.user ?? {};
  const channels = user.channels ?? {};
  return (
    <div className="flex flex-col gap-3 rounded-lg border border-border p-4" data-testid="channels-tab">
      <h3 className="text-sm font-medium">Channels</h3>
      <p className="text-xs text-muted-foreground">
        Force-clear wipes the encrypted session blob on disk even mid-
        reconnect. Operators use this after a security incident; the user
        must re-pair the channel afterwards.
      </p>
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead className="w-32">Channel</TableHead>
            <TableHead className="w-32">Status</TableHead>
            <TableHead className="w-40">Device label</TableHead>
            <TableHead className="w-40">Last sync</TableHead>
            <TableHead className="w-48">Action</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {CHANNEL_IDS.map((id) => (
            <ChannelRow
              key={id}
              userId={userId}
              channelId={id}
              state={channels[id]}
              onChange={onChange}
            />
          ))}
        </TableBody>
      </Table>
    </div>
  );
}

function ChannelRow({
  userId,
  channelId,
  state,
  onChange,
}: {
  userId: string;
  channelId: ChannelId;
  state: ChannelState | undefined;
  onChange: () => Promise<void>;
}) {
  const [busy, setBusy] = useState(false);
  const [rowError, setRowError] = useState<string | null>(null);
  const [confirmPhrase, setConfirmPhrase] = useState("");
  const [showConfirm, setShowConfirm] = useState(false);

  async function handleForceClear() {
    setBusy(true);
    setRowError(null);
    try {
      await disconnectChannel(TOKEN_GETTER, userId, channelId);
      setShowConfirm(false);
      setConfirmPhrase("");
      await onChange();
    } catch (e) {
      setRowError(errMsg(e, "Failed to clear channel session"));
    } finally {
      setBusy(false);
    }
  }

  const statusVariant: Parameters<typeof Badge>[0]["variant"] =
    state?.status === "connected"
      ? "default"
      : state?.status === "pending"
        ? "secondary"
        : state?.status === "failed"
          ? "destructive"
          : "outline";

  return (
    <TableRow data-testid={`channel-row-${channelId}`}>
      <TableCell className="font-mono text-xs">{channelId}</TableCell>
      <TableCell>
        <Badge variant={statusVariant} data-testid={`channel-status-${channelId}`}>
          {state?.status ?? "disconnected"}
        </Badge>
      </TableCell>
      <TableCell className="text-xs text-muted-foreground">
        {state?.deviceLabel ?? "—"}
      </TableCell>
      <TableCell className="text-xs text-muted-foreground">
        {state?.lastSync ? new Date(state.lastSync).toLocaleString() : "—"}
      </TableCell>
      <TableCell>
        {showConfirm ? (
          <div className="flex items-center gap-1">
            <Input
              placeholder="Type CONFIRM"
              value={confirmPhrase}
              onChange={(e) => setConfirmPhrase(e.target.value)}
              data-testid={`channel-confirm-input-${channelId}`}
              className="w-32"
            />
            <Button
              variant="destructive"
              size="sm"
              disabled={busy || confirmPhrase.trim() !== "CONFIRM"}
              onClick={handleForceClear}
              data-testid={`channel-confirm-clear-${channelId}`}
            >
              {busy && <Loader2 className="mr-1 h-3 w-3 animate-spin" />}
              Clear
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => {
                setShowConfirm(false);
                setConfirmPhrase("");
              }}
              disabled={busy}
            >
              Cancel
            </Button>
          </div>
        ) : (
          <Button
            variant="outline"
            size="sm"
            onClick={() => setShowConfirm(true)}
            data-testid={`channel-force-clear-${channelId}`}
          >
            <Ban className="mr-1 h-3 w-3" /> Force-clear session
          </Button>
        )}
        {rowError && (
          <p className="mt-1 text-xs text-destructive" data-testid={`channel-error-${channelId}`}>
            {rowError}
          </p>
        )}
      </TableCell>
    </TableRow>
  );
}

// ─── Notifications (Push) tab ────────────────────────────────────────

function NotificationsTab({
  userId,
  config,
}: {
  userId: string;
  config: OpenclawConfig;
}) {
  const subs = config.user?.push?.subscriptions ?? [];
  const [events, setEvents] = useState<PushEvent[]>([]);
  const [loading, setLoading] = useState(false);
  const [tabError, setTabError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const r = await getRecentPushEvents(TOKEN_GETTER, userId);
        if (!cancelled) setEvents(r.events);
      } catch (e) {
        if (!cancelled) setTabError(errMsg(e, "Failed to load recent push events"));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [userId]);

  async function handleTest(subId: string) {
    setTabError(null);
    try {
      await testPushSubscription(TOKEN_GETTER, userId, subId);
    } catch (e) {
      setTabError(errMsg(e, "Test push failed"));
    }
  }

  return (
    <div className="flex flex-col gap-3 rounded-lg border border-border p-4" data-testid="push-tab">
      <h3 className="text-sm font-medium">Push subscriptions</h3>
      {tabError && (
        <div className="rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2 text-sm text-destructive" data-testid="push-tab-error">
          {tabError}
        </div>
      )}
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead className="w-40">Device</TableHead>
            <TableHead className="w-40">Browser</TableHead>
            <TableHead className="w-48">Subscribed</TableHead>
            <TableHead className="w-32">Action</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {subs.length === 0 && (
            <TableRow>
              <TableCell colSpan={4} className="text-sm text-muted-foreground">
                No active push subscriptions.
              </TableCell>
            </TableRow>
          )}
          {subs.map((s) => (
            <TableRow key={s.id} data-testid={`push-sub-row-${s.id}`}>
              <TableCell>{s.deviceLabel ?? "—"}</TableCell>
              <TableCell className="text-xs text-muted-foreground">
                {s.browser ?? "—"}
              </TableCell>
              <TableCell className="text-xs text-muted-foreground">
                {s.createdAt ? new Date(s.createdAt).toLocaleString() : "—"}
              </TableCell>
              <TableCell>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => void handleTest(s.id)}
                  data-testid={`push-test-${s.id}`}
                >
                  <Megaphone className="mr-1 h-3 w-3" /> Send test
                </Button>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>

      <h4 className="mt-3 text-xs font-medium uppercase text-muted-foreground">
        Recent events (last 50)
      </h4>
      {loading ? (
        <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
      ) : events.length === 0 ? (
        <p className="text-sm text-muted-foreground">No recent push events.</p>
      ) : (
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-32">Type</TableHead>
              <TableHead className="w-40">Device</TableHead>
              <TableHead>Time</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {events.map((e) => (
              <TableRow key={e.id} data-testid={`push-event-${e.id}`}>
                <TableCell>
                  <Badge variant="outline">{e.type}</Badge>
                </TableCell>
                <TableCell className="text-xs">{e.deviceLabel ?? "—"}</TableCell>
                <TableCell className="text-xs text-muted-foreground">
                  {new Date(e.timestamp).toLocaleString()}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
    </div>
  );
}

// ─── Cron tab ────────────────────────────────────────────────────────

function CronTab({ userId }: { userId: string }) {
  const [jobs, setJobs] = useState<CronJob[]>([]);
  const [loading, setLoading] = useState(false);
  const [tabError, setTabError] = useState<string | null>(null);
  const [expandedJob, setExpandedJob] = useState<string | null>(null);
  const [runs, setRuns] = useState<Record<string, CronRun[]>>({});

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      const r = await listCronJobs(TOKEN_GETTER, userId);
      setJobs(r.jobs);
      setTabError(null);
    } catch (e) {
      setTabError(errMsg(e, "Failed to load cron jobs"));
    } finally {
      setLoading(false);
    }
  }, [userId]);

  useEffect(() => {
    void reload();
  }, [reload]);

  async function handleRunNow(jobId: string) {
    setTabError(null);
    try {
      await runCronJob(TOKEN_GETTER, userId, jobId);
      await reload();
    } catch (e) {
      setTabError(errMsg(e, "Failed to start cron run"));
    }
  }

  async function handleCancel(jobId: string) {
    if (!confirm(`Force-cancel running job ${jobId}? Best-effort — harness-relayed jobs may not interrupt mid-LLM-call.`))
      return;
    setTabError(null);
    try {
      await cancelCronJob(TOKEN_GETTER, userId, jobId);
      await reload();
    } catch (e) {
      setTabError(errMsg(e, "Cancel failed (the runtime may not have shipped P6 yet)"));
    }
  }

  async function toggleExpand(jobId: string) {
    if (expandedJob === jobId) {
      setExpandedJob(null);
      return;
    }
    setExpandedJob(jobId);
    if (!runs[jobId]) {
      try {
        const r = await getCronJobRuns(TOKEN_GETTER, userId, jobId);
        setRuns((prev) => ({ ...prev, [jobId]: r.runs }));
      } catch (e) {
        setTabError(errMsg(e, `Failed to load runs for ${jobId}`));
      }
    }
  }

  return (
    <div className="flex flex-col gap-3 rounded-lg border border-border p-4" data-testid="cron-tab">
      <h3 className="text-sm font-medium">Cron jobs</h3>
      {tabError && (
        <div className="rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2 text-sm text-destructive" data-testid="cron-tab-error">
          {tabError}
        </div>
      )}
      {loading ? (
        <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
      ) : (
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Label</TableHead>
              <TableHead className="w-32">Schedule</TableHead>
              <TableHead className="w-32">Last run</TableHead>
              <TableHead className="w-32">Next run</TableHead>
              <TableHead className="w-48">Action</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {jobs.length === 0 && (
              <TableRow>
                <TableCell colSpan={5} className="text-sm text-muted-foreground">
                  No cron jobs. (Endpoint may return empty until lexruntime P6
                  ships — see handoff §11.)
                </TableCell>
              </TableRow>
            )}
            {jobs.map((job) => {
              const isRunning = job.lastRunStatus === "running";
              const isExpanded = expandedJob === job.jobId;
              return (
                <>
                  <TableRow
                    key={job.jobId}
                    data-testid={`cron-job-row-${job.jobId}`}
                  >
                    <TableCell>
                      <button
                        type="button"
                        className="flex items-center gap-1 hover:underline"
                        onClick={() => void toggleExpand(job.jobId)}
                      >
                        <ChevronRight
                          className={`h-3 w-3 transition-transform ${isExpanded ? "rotate-90" : ""}`}
                        />
                        {job.label}
                      </button>
                    </TableCell>
                    <TableCell className="font-mono text-xs">{job.schedule}</TableCell>
                    <TableCell>
                      <Badge
                        variant={
                          job.lastRunStatus === "ok"
                            ? "default"
                            : job.lastRunStatus === "failed"
                              ? "destructive"
                              : "outline"
                        }
                      >
                        {job.lastRunStatus ?? "—"}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-xs text-muted-foreground">
                      {job.nextRunAt ? new Date(job.nextRunAt).toLocaleString() : "—"}
                    </TableCell>
                    <TableCell className="flex items-center gap-1">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => void handleRunNow(job.jobId)}
                        data-testid={`cron-run-${job.jobId}`}
                      >
                        <Play className="mr-1 h-3 w-3" /> Run
                      </Button>
                      {isRunning && (
                        <Button
                          variant="destructive"
                          size="sm"
                          onClick={() => void handleCancel(job.jobId)}
                          data-testid={`cron-cancel-${job.jobId}`}
                        >
                          <Square className="mr-1 h-3 w-3" /> Cancel
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                  {isExpanded && (
                    <TableRow key={`${job.jobId}-runs`}>
                      <TableCell colSpan={5} className="bg-panel-2 p-3">
                        <h4 className="mb-1 text-xs uppercase text-muted-foreground">
                          Run history
                        </h4>
                        {!runs[job.jobId] ? (
                          <Loader2 className="h-3 w-3 animate-spin text-muted-foreground" />
                        ) : runs[job.jobId].length === 0 ? (
                          <p className="text-xs text-muted-foreground">No runs yet.</p>
                        ) : (
                          <ul className="space-y-1 text-xs">
                            {runs[job.jobId].map((r) => (
                              <li key={r.runId} className="font-mono">
                                <Badge variant={r.status === "ok" ? "default" : "destructive"}>
                                  {r.status}
                                </Badge>{" "}
                                {new Date(r.startedAt).toLocaleString()}{" "}
                                {r.error && (
                                  <span className="text-destructive">— {r.error}</span>
                                )}
                              </li>
                            ))}
                          </ul>
                        )}
                      </TableCell>
                    </TableRow>
                  )}
                </>
              );
            })}
          </TableBody>
        </Table>
      )}
    </div>
  );
}

// ─── Browser tab ─────────────────────────────────────────────────────

function BrowserTab({
  userId,
  config,
  onChange,
}: {
  userId: string;
  config: OpenclawConfig;
  onChange: () => Promise<void>;
}) {
  const profiles = config.user?.browser?.profiles ?? [];
  const activeId = config.user?.browser?.activeProfileId ?? null;
  const activeProfile = profiles.find((p) => p.id === activeId) ?? null;
  return (
    <div className="flex flex-col gap-3 rounded-lg border border-border p-4" data-testid="browser-tab">
      <h3 className="text-sm font-medium">Browser profiles</h3>
      <p className="text-xs text-muted-foreground">
        Force-stop kills the live Chromium even when the user has the noVNC
        pane open — any in-flight chat turns using the browser tool will
        fail-fast.
      </p>
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Label</TableHead>
            <TableHead className="w-40">ID</TableHead>
            <TableHead className="w-32">Active</TableHead>
            <TableHead className="w-48">Last used</TableHead>
            <TableHead className="w-56">Action</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {profiles.length === 0 && (
            <TableRow>
              <TableCell colSpan={5} className="text-sm text-muted-foreground">
                No browser profiles.
              </TableCell>
            </TableRow>
          )}
          {profiles.map((p) => (
            <BrowserProfileRow
              key={p.id}
              userId={userId}
              profile={p}
              isActive={p.id === activeId}
              onChange={onChange}
            />
          ))}
        </TableBody>
      </Table>

      {activeProfile && (
        <NoVncPane userId={userId} profile={activeProfile} />
      )}
    </div>
  );
}

/**
 * NoVncPane — operator-facing viewer for a user's live Chromium (P4.D.X, the
 * PLATFORM_ADMIN cross-user view; shipped on top of lexruntime P4.D.W).
 *
 * Fetches GET /browser/status (cross-user via X-Target-User-Id) for the active
 * profile's plugin-relative noVNC endpoint, then embeds <LiveBrowserCanvas>,
 * which mints the short-TTL ticket, opens the lexserver tunnel
 * (`/ws/runtime/<targetUserId>/api/lx/openclaw/browser/vnc/ws`) with the
 * operator's `platform.admin` Lexiun JWT in the subprotocol, and paints the
 * RFB framebuffer. lexserver authorises the cross-user view (scope check) and
 * writes a `platform.runtime.vnc.view` audit row.
 *
 * Security (handoff §7 / §10): the warning banner is always rendered; the
 * short-TTL ticket lives only inside the canvas (ref + WS URL) — never
 * console.log'd, never localStorage, never rendered to any surface.
 */
const NOVNC_STATUS_LABEL: Record<LiveBrowserStatus, string> = {
  idle: "idle",
  connecting: "connecting…",
  connected: "live",
  disconnected: "disconnected",
  error: "error",
};

function NoVncPane({
  userId,
  profile,
}: {
  userId: string;
  profile: BrowserProfile;
}) {
  const [status, setStatus] = useState<LxBrowserStatus | null>(null);
  const [loading, setLoading] = useState(false);
  const [paneError, setPaneError] = useState<string | null>(null);
  const [canvasStatus, setCanvasStatus] = useState<LiveBrowserStatus>("idle");

  const refresh = useCallback(async () => {
    setLoading(true);
    setPaneError(null);
    try {
      setStatus(await getBrowserStatus(TOKEN_GETTER, userId));
    } catch (e) {
      setPaneError(errMsg(e, "Failed to fetch browser status"));
    } finally {
      setLoading(false);
    }
  }, [userId]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  // The runtime reports a plugin-relative endpoint; LiveBrowserCanvas prefixes
  // the lexserver tunnel segment + the viewed user's id and anchors it to the
  // lexserver WS host. `null` when the browser is disabled / no active profile.
  const vncEndpoint = status?.vnc?.endpoint ?? status?.vncEndpoint ?? null;

  return (
    <div className="rounded-md border border-border bg-panel-2 p-3" data-testid="novnc-pane">
      <div className="mb-2 flex items-center justify-between gap-2">
        <h4 className="flex items-center gap-2 text-xs font-medium uppercase text-muted-foreground">
          Live Chromium — {profile.label}
          <span
            className={`rounded px-1.5 py-0.5 text-[10px] normal-case ${
              canvasStatus === "connected"
                ? "bg-success/15 text-success"
                : canvasStatus === "error"
                  ? "bg-destructive/15 text-destructive"
                  : "bg-muted text-muted-foreground"
            }`}
            data-testid="novnc-status"
            data-status={canvasStatus}
          >
            {NOVNC_STATUS_LABEL[canvasStatus]}
          </span>
        </h4>
        <Button
          variant="outline"
          size="sm"
          onClick={() => void refresh()}
          disabled={loading}
          data-testid="novnc-refresh"
        >
          <RefreshCw className={`mr-1 h-3 w-3 ${loading ? "animate-spin" : ""}`} />
          Reload
        </Button>
      </div>

      <div className="mb-2 rounded-md border border-warning/40 bg-warning/10 px-2 py-1 text-xs text-warning">
        ⚠ Anything done in this browser uses the user's saved logins on
        court portals / OAB / tax sites. Treat as if you're sitting at
        their desk. This cross-user view is audited.
      </div>

      {paneError && (
        <div className="mb-2 rounded-md border border-destructive/40 bg-destructive/10 px-2 py-1 text-xs text-destructive" data-testid="novnc-error">
          {paneError}
        </div>
      )}

      {vncEndpoint ? (
        <div
          className="relative h-[480px] w-full overflow-hidden rounded-md border border-border bg-black"
          data-testid="novnc-canvas-frame"
        >
          <LiveBrowserCanvas
            targetUserId={userId}
            vncEndpoint={vncEndpoint}
            onStatusChange={(s) => setCanvasStatus(s)}
            className="h-full w-full"
          />
        </div>
      ) : (
        <div
          className="rounded-md border border-border bg-canvas p-3 text-xs text-muted-foreground"
          data-testid="novnc-unavailable"
        >
          Live view unavailable — the browser is disabled for this user, or no
          profile is active. Launch a profile, then Reload.
        </div>
      )}
    </div>
  );
}

function BrowserProfileRow({
  userId,
  profile,
  isActive,
  onChange,
}: {
  userId: string;
  profile: BrowserProfile;
  isActive: boolean;
  onChange: () => Promise<void>;
}) {
  type Busy =
    | "launch"
    | "stop"
    | "pause"
    | "resume"
    | "wipe"
    | null;
  const [busy, setBusy] = useState<Busy>(null);
  const [rowError, setRowError] = useState<string | null>(null);
  const [confirmStop, setConfirmStop] = useState(false);
  const [wipeConfirm, setWipeConfirm] = useState("");
  const [showWipe, setShowWipe] = useState(false);

  async function wrap<T>(kind: Exclude<Busy, null>, fn: () => Promise<T>, label: string) {
    setBusy(kind);
    setRowError(null);
    try {
      await fn();
      await onChange();
    } catch (e) {
      setRowError(errMsg(e, `${label} failed`));
    } finally {
      setBusy(null);
    }
  }

  const handleLaunch = () =>
    wrap("launch", () => launchBrowserProfile(TOKEN_GETTER, userId, profile.id), "Launch");
  const handleForceStop = async () => {
    await wrap("stop", () => stopBrowserProfile(TOKEN_GETTER, userId, profile.id), "Stop");
    setConfirmStop(false);
  };
  const handlePause = () =>
    wrap("pause", () => pauseBrowserProfile(TOKEN_GETTER, userId, profile.id), "Pause");
  const handleResume = () =>
    wrap("resume", () => resumeBrowserProfile(TOKEN_GETTER, userId, profile.id), "Resume");
  const handleWipe = async () => {
    await wrap("wipe", () => wipeBrowserProfileCookies(TOKEN_GETTER, userId, profile.id), "Wipe");
    setShowWipe(false);
    setWipeConfirm("");
  };

  return (
    <TableRow data-testid={`browser-profile-row-${profile.id}`}>
      <TableCell>{profile.label}</TableCell>
      <TableCell className="font-mono text-xs text-muted-foreground">
        {profile.id}
      </TableCell>
      <TableCell>
        {isActive ? (
          <Badge variant="default" data-testid={`browser-active-${profile.id}`}>
            active
          </Badge>
        ) : (
          <Badge variant="outline">idle</Badge>
        )}
      </TableCell>
      <TableCell className="text-xs text-muted-foreground">
        {profile.lastUsedAt ? new Date(profile.lastUsedAt).toLocaleString() : "—"}
      </TableCell>
      <TableCell>
        <div className="flex flex-wrap items-center gap-1">
          {!isActive && (
            <Button
              variant="outline"
              size="sm"
              onClick={handleLaunch}
              disabled={busy !== null}
              data-testid={`browser-launch-${profile.id}`}
            >
              {busy === "launch" && <Loader2 className="mr-1 h-3 w-3 animate-spin" />}
              <Play className="mr-1 h-3 w-3" /> Launch
            </Button>
          )}
          {/* Pause / Resume — operator-grade affordance shipping with the
              runtime-side P10 slice. Wires before P10 lands so the UI is
              ready the moment the runtime image rolls. Both buttons are
              always offered (since the runtime hasn't pinned which field
              surfaces the agentPaused state to operators yet); clicking
              the wrong one returns a benign error from the runtime. */}
          {isActive && (
            <>
              <Button
                variant="outline"
                size="sm"
                onClick={handlePause}
                disabled={busy !== null}
                data-testid={`browser-pause-${profile.id}`}
                title="Flip the conditional GATE's agentPaused flag — the agent can't use this profile until resumed."
              >
                {busy === "pause" && <Loader2 className="mr-1 h-3 w-3 animate-spin" />}
                Pause
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={handleResume}
                disabled={busy !== null}
                data-testid={`browser-resume-${profile.id}`}
              >
                {busy === "resume" && <Loader2 className="mr-1 h-3 w-3 animate-spin" />}
                Resume
              </Button>
            </>
          )}
          {isActive &&
            (confirmStop ? (
              <>
                <Button
                  variant="destructive"
                  size="sm"
                  onClick={handleForceStop}
                  disabled={busy !== null}
                  data-testid={`browser-confirm-stop-${profile.id}`}
                >
                  {busy === "stop" && <Loader2 className="mr-1 h-3 w-3 animate-spin" />}
                  Confirm force-stop
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setConfirmStop(false)}
                  disabled={busy !== null}
                >
                  Cancel
                </Button>
              </>
            ) : (
              <Button
                variant="outline"
                size="sm"
                onClick={() => setConfirmStop(true)}
                data-testid={`browser-force-stop-${profile.id}`}
              >
                <Square className="mr-1 h-3 w-3" /> Force-stop
              </Button>
            ))}
          {/* Wipe cookies — the only revocation primitive (handoff §8
              threat model J). Gated by typed CONFIRM since the
              userDataDir is unrecoverable once removed. */}
          {showWipe ? (
            <>
              <Input
                placeholder="Type CONFIRM"
                value={wipeConfirm}
                onChange={(e) => setWipeConfirm(e.target.value)}
                data-testid={`browser-wipe-input-${profile.id}`}
                className="w-32"
              />
              <Button
                variant="destructive"
                size="sm"
                disabled={busy !== null || wipeConfirm.trim() !== "CONFIRM"}
                onClick={handleWipe}
                data-testid={`browser-confirm-wipe-${profile.id}`}
              >
                {busy === "wipe" && <Loader2 className="mr-1 h-3 w-3 animate-spin" />}
                Wipe cookies
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  setShowWipe(false);
                  setWipeConfirm("");
                }}
                disabled={busy !== null}
              >
                Cancel
              </Button>
            </>
          ) : (
            <Button
              variant="outline"
              size="sm"
              onClick={() => setShowWipe(true)}
              data-testid={`browser-wipe-${profile.id}`}
              title="Removes the on-disk userDataDir (cookies + cached creds). Unrecoverable."
            >
              Wipe cookies
            </Button>
          )}
        </div>
        {rowError && (
          <p className="mt-1 text-xs text-destructive" data-testid={`browser-error-${profile.id}`}>
            {rowError}
          </p>
        )}
      </TableCell>
    </TableRow>
  );
}

// ─── Voice tab ───────────────────────────────────────────────────────

function VoiceTab({
  userId,
  config,
  onSaved,
}: {
  userId: string;
  config: OpenclawConfig;
  onSaved: () => Promise<void>;
}) {
  const policy = config.org.userSettingsPolicy ?? {};
  const voiceDisabled = policy.voice === false;
  const allowed = config.org.voice?.allowedVoices ?? [];
  const tts = config.user?.voice?.tts ?? {};
  const [enabled, setEnabled] = useState(tts.enabled ?? false);
  const [voice, setVoice] = useState(tts.selectedVoice ?? config.org.voice?.defaultVoice ?? "");
  const [busy, setBusy] = useState(false);
  const [tabError, setTabError] = useState<string | null>(null);

  async function handleSave() {
    setBusy(true);
    setTabError(null);
    try {
      await patchOpenclawUserConfig(TOKEN_GETTER, userId, {
        voice: { tts: { enabled, selectedVoice: voice || undefined } },
      });
      await onSaved();
    } catch (e) {
      setTabError(errMsg(e, "Failed to save voice settings"));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="flex flex-col gap-3 rounded-lg border border-border p-4" data-testid="voice-tab">
      <h3 className="flex items-center gap-2 text-sm font-medium">
        <Headphones className="h-4 w-4" /> Voice
      </h3>
      {voiceDisabled && (
        <Badge variant="outline" data-testid="voice-locked">
          locked by org (userSettingsPolicy.voice=false)
        </Badge>
      )}
      {tabError && (
        <div className="rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2 text-sm text-destructive" data-testid="voice-tab-error">
          {tabError}
        </div>
      )}
      <label className="flex items-center gap-2 text-sm">
        <input
          type="checkbox"
          checked={enabled}
          disabled={voiceDisabled || busy}
          onChange={(e) => setEnabled(e.target.checked)}
          data-testid="voice-enabled"
        />
        TTS enabled
      </label>
      <label className="flex flex-col gap-1">
        <span className="text-xs text-muted-foreground">
          Voice (org-curated slate)
        </span>
        <select
          value={voice}
          disabled={voiceDisabled || busy || allowed.length === 0}
          onChange={(e) => setVoice(e.target.value)}
          data-testid="voice-picker"
          className="rounded-md border border-border bg-panel-2 px-2 py-1 text-sm"
        >
          <option value="">(use org default)</option>
          {allowed.map((v) => (
            <option key={v} value={v}>{v}</option>
          ))}
        </select>
      </label>
      <Button
        onClick={handleSave}
        disabled={busy || voiceDisabled}
        data-testid="voice-save-btn"
        className="self-start"
      >
        {busy && <Loader2 className="mr-1 h-4 w-4 animate-spin" />}
        Save
      </Button>
    </div>
  );
}

// ─── Agent tab (read-only) ───────────────────────────────────────────

function AgentTab({ config }: { config: OpenclawConfig }) {
  const id = config.org.agents?.defaultIdentity ?? {};
  return (
    <div className="flex flex-col gap-3 rounded-lg border border-border p-4" data-testid="agent-tab">
      <h3 className="flex items-center gap-2 text-sm font-medium">
        <Bot className="h-4 w-4" /> Agent identity (org rail — read-only)
      </h3>
      <p className="text-xs text-muted-foreground">
        Editing org-side agent identity uses the lexserver{" "}
        <code className="font-mono">PATCH /api/orgs/:orgId/runtime-config</code>{" "}
        rail. Drill into the org from <Link to="/orgs" className="text-primary underline">Organizations</Link>.
      </p>
      <dl className="grid grid-cols-2 gap-2 text-sm">
        <dt className="text-muted-foreground">Default name</dt>
        <dd>{id.name ?? "—"}</dd>
        <dt className="text-muted-foreground">Default avatar URL</dt>
        <dd className="font-mono text-xs">{id.avatarUrl ?? "—"}</dd>
      </dl>
    </div>
  );
}

// ─── Memory & Tools tab (read-only) ──────────────────────────────────

function MemoryToolsTab({ config }: { config: OpenclawConfig }) {
  return (
    <div className="flex flex-col gap-3 rounded-lg border border-border p-4" data-testid="memory-tab">
      <h3 className="flex items-center gap-2 text-sm font-medium">
        <Wrench className="h-4 w-4" /> Memory &amp; Tools (org rail — read-only)
      </h3>
      <p className="text-xs text-muted-foreground">
        Tier-B-org block. Edits land via the lexserver org rail (drill into
        the org from Organizations). The Tier-A floor (locked-by-platform)
        is pinned in the lexruntime entrypoint and not visible here.
      </p>
      <details className="text-xs">
        <summary className="cursor-pointer text-muted-foreground">
          Raw org.memory + org.tools (JSON)
        </summary>
        <pre className="mt-1 whitespace-pre-wrap rounded bg-panel-2 p-2 font-mono">
          {JSON.stringify(
            { memory: config.org.memory ?? null, tools: config.org.tools ?? null },
            null,
            2,
          )}
        </pre>
      </details>
    </div>
  );
}

// ─── Audit-log viewer (handoff §4.3) ─────────────────────────────────

const LX_OPENCLAW_SUBSYSTEMS = [
  "lx-openclaw-config",
  "lx-openclaw-channels",
  "lx-openclaw-push",
  "lx-openclaw-browser",
  "lx-openclaw-cron",
] as const;

type SubsystemFilter = (typeof LX_OPENCLAW_SUBSYSTEMS)[number] | "all";

function AuditLogTab({ userId }: { userId: string }) {
  const api = usePlatformApi();
  const [subsystem, setSubsystem] = useState<SubsystemFilter>("all");
  const [q, setQ] = useState("");
  const [logs, setLogs] = useState<OperationalLogRecord[]>([]);
  const [loading, setLoading] = useState(false);
  const [tabError, setTabError] = useState<string | null>(null);
  const [expanded, setExpanded] = useState<string | null>(null);

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      const filter: OperationalLogFilter = {
        // Scope to the target user — surfaces rows the runtime stamped
        // with `updatedBy: <userId>` AND lexserver's
        // `platform.runtime.proxy` rows whose `entityId` is the target.
        // The container_owner_user_id filter is the LX-correct one for
        // runtime-emitted rows; the q free-text catches the rest.
        container_owner_user_id: userId,
        limit: 100,
      };
      if (subsystem !== "all") filter.subsystem = subsystem;
      if (q.trim()) filter.q = q.trim();
      const res = await api.listOperationalLogs(filter);
      setLogs(res.data.records ?? []);
      setTabError(null);
    } catch (e) {
      setTabError(e instanceof Error ? e.message : String(e));
    } finally {
      setLoading(false);
    }
  }, [api, userId, subsystem, q]);

  useEffect(() => {
    void reload();
  }, [reload]);

  const orderedLogs = useMemo(
    () =>
      [...logs].sort((a, b) =>
        (b.timestamp_ms ?? 0) - (a.timestamp_ms ?? 0),
      ),
    [logs],
  );

  return (
    <div className="flex flex-col gap-3 rounded-lg border border-border p-4" data-testid="audit-tab">
      <h3 className="flex items-center gap-2 text-sm font-medium">
        <ClipboardList className="h-4 w-4" /> Audit log
      </h3>
      <p className="text-xs text-muted-foreground">
        Filters rows tagged{" "}
        <code className="font-mono text-foreground">subsystem=lx-openclaw-*</code>{" "}
        for this user (handoff §4.3). Operator-side proxy hops
        (<code className="font-mono">platform.runtime.proxy</code>) also
        surface here when free-text matches the action name.
      </p>

      <div className="flex items-center gap-2 flex-wrap">
        <label className="flex items-center gap-1 text-xs text-muted-foreground">
          Subsystem
          <select
            value={subsystem}
            onChange={(e) => setSubsystem(e.target.value as SubsystemFilter)}
            data-testid="audit-subsystem-filter"
            className="rounded-md border border-border bg-panel-2 px-2 py-1 text-xs"
          >
            {/* Phase 21.E — the default "all" sends no subsystem filter, so
                the result is every subsystem for this user (gateway,
                lx-llm-resolver, harn-service, plugin/startup, the 5
                lx-openclaw-* tags, etc.) — NOT just the lx-openclaw-*
                family. The label reflects that; pick a specific lx-
                openclaw-* tag to scope tightly. The /api/platform/logs
                endpoint takes a single `subsystem` string, so a true
                family filter would need either backend regex support or
                5 parallel requests + merge — deferred. */}
            <option value="all">all (any subsystem for this user)</option>
            {LX_OPENCLAW_SUBSYSTEMS.map((s) => (
              <option key={s} value={s}>{s}</option>
            ))}
          </select>
        </label>
        <Input
          placeholder="Free-text search (action / message / actor)"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          data-testid="audit-q"
          className="w-64"
        />
        <Button
          variant="outline"
          size="sm"
          onClick={() => void reload()}
          disabled={loading}
          data-testid="audit-reload"
        >
          <Search className="mr-1 h-3 w-3" />
          Refresh
        </Button>
      </div>

      {tabError && (
        <div className="rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2 text-sm text-destructive" data-testid="audit-tab-error">
          {tabError}
        </div>
      )}

      {loading ? (
        <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
      ) : (
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-44">When</TableHead>
              <TableHead className="w-36">Subsystem</TableHead>
              <TableHead className="w-20">Level</TableHead>
              <TableHead>Message</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {orderedLogs.length === 0 && (
              <TableRow>
                <TableCell colSpan={4} className="text-sm text-muted-foreground">
                  No audit rows match the current filters.
                </TableCell>
              </TableRow>
            )}
            {orderedLogs.map((log) => {
              const id = log._id ?? log.trace_id ?? String(log.timestamp_ms);
              const isOpen = expanded === id;
              return (
                <>
                  <TableRow
                    key={id}
                    className="cursor-pointer hover:bg-accent/40"
                    onClick={() => setExpanded(isOpen ? null : id)}
                    data-testid={`audit-row-${id}`}
                  >
                    <TableCell className="font-mono text-xs">
                      {log.timestamp_ms
                        ? new Date(log.timestamp_ms).toLocaleString()
                        : "—"}
                    </TableCell>
                    <TableCell>
                      <Badge variant="outline" className="font-mono text-xs">
                        {log.subsystem ?? "—"}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <Badge
                        variant={
                          log.level === "error"
                            ? "destructive"
                            : log.level === "warn"
                              ? "secondary"
                              : "outline"
                        }
                      >
                        {log.level ?? "info"}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-xs">
                      <span className="font-mono">{log.message}</span>
                    </TableCell>
                  </TableRow>
                  {isOpen && (
                    <TableRow key={`${id}-detail`}>
                      <TableCell colSpan={4} className="bg-panel-2 p-3">
                        <pre className="whitespace-pre-wrap text-xs font-mono">
                          {JSON.stringify(log, null, 2)}
                        </pre>
                      </TableCell>
                    </TableRow>
                  )}
                </>
              );
            })}
          </TableBody>
        </Table>
      )}
    </div>
  );
}

// silence unused-import warnings for utility types not directly consumed in this file
export type { UserState };
